/**
 * ProjectDetailsTemplate Component
 *
 * Pure presentation component for the Project Details page.
 * Displays project information, chat input, AI hints, and conversation history.
 *
 * DRAMATICALLY SIMPLIFIED with Facade Pattern!
 *
 * Before: 40+ individual props (state, actions, handlers...)
 * After: 1 facade + 3-4 platform-specific props
 *
 * @package @ideoz/ui
 */

import React from 'react';
import { CircleAlert, FileText, FolderPlus, ReplaceAll } from 'lucide-react';
import {
  ConversationMode,
  type ProjectDetailsPageFacade,
  ProjectMode,
  useCreditLimitBanner,
} from '@ideoz/core';
import { googleDriveService } from '@ideoz/core/api/services/googleDrive.service';
import {
  Header,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Icon,
  showToast,
  QuickActionChips,
  type QuickAction,
} from '../index';
import { CreateFirstProjectDialog } from '../organisms/CreateFirstProjectDialog';
import UpgradeCtaCard from '../molecules/UpgradeCtaCard';
import { toast } from 'sonner';
import { AppLayout } from '../layouts/AppLayout';
import ChatTextAreaInternal from '../molecules/ChatTextArea';
import { ProjectConversationsList } from '../organisms/ProjectConversationsList';
import { ProjectDetailsModal } from '../organisms/ProjectDetailsModal';
import { UploadFilesDialog, type GoogleAuthConfig } from '../organisms/UploadFilesDialog';
import { useFileAttachments } from '../../hooks/useFileAttachments';
import DesignSystemDrawer from '../organisms/DesignSystemDrawer';
import { SyncProgressToast } from '../molecules/SyncProgressToast';
import { cn } from '@ideoz/ui';

/**
 * Project form data interface (for modal)
 */
export interface ProjectFormData {
  name: string;
  description: string;
  main_persona: string;
  design_process: string;
  ideoz_role: string;
  user_role: string;
  industry: string;
}

/**
 * ProjectDetailsTemplate Props
 *
 * DRAMATICALLY SIMPLIFIED with Facade Pattern!
 *
 * Before: 40+ individual props (state, actions, handlers...)
 * After: 1 facade + 4-5 platform-specific props
 *
 * The facade encapsulates all shared logic following useLandingPageFacade pattern:
 * - State (isLoggedIn)
 * - Header configuration & user menu
 * - Navigation (toLanding, toConversation, toProfile, etc.)
 * - Conversation actions (create, reset, etc.)
 * - Project Details (currentProject, conversations, attachments, drawer, etc.)
 */
export interface ProjectDetailsTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: ProjectDetailsPageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Translation function
  t: (key: string) => string;

  // Google Auth config (only for web)
  googleAuthConfig?: GoogleAuthConfig;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    isLoading: boolean;
    openRegister: () => void;
    openLogin: () => void;
    closeDialog: () => void;
    switchMode: (mode: 'login' | 'register') => void;
    handleLogin: (email: string, password: string) => Promise<void>;
    handleRegister: (name: string, email: string, password: string) => Promise<void>;
    handleGoogleAuth: () => Promise<void>;
  };

  // Plugin-specific
  pluginLogin?: any;
  pluginStopInterval?: () => void;
  isAuthLoading?: boolean;
}

/**
 * ProjectDetailsTemplate Component
 *
 * Displays project details page with:
 * - Header with project name
 * - Title section asking about problems
 * - Chat input area
 * - AI-generated prompt hints
 * - "Need smarter help?" section with action buttons
 * - Previous conversations list
 * - Modals for editing project and uploading files
 */
export const ProjectDetailsTemplate: React.FC<ProjectDetailsTemplateProps> = ({
  facade,
  isWeb,
  t = (key: string) => key,
  googleAuthConfig,
  authDialogState,
  pluginLogin,
  pluginStopInterval,
  isAuthLoading = false,
}) => {
  // ========== Destructure Facade for Clean Usage ==========
  const {
    state: { isLoggedIn, currentMode, llmFamily, planDetails },
    header: {
      headerProps,
      userMenuItems = [],
      userMenuSocialLinks = [],
      isRequestingUpgrade = false,
    },
    navigation: {
      toLanding: onBack,
      toProjectsList: onNavigateToProjectsList,
      toConversation: onNavigateToConversation,
    },
    handleBack,
    conversation: {
      create: onCreateConversation,
      reset: onResetConversation,
      setLlmFamily: onSetLlmFamily,
      setName: onSetConversationName,
      setId: onSetConversationId,
      setMode: onSetConversationMode,
    },
    projectDetails: {
      currentProject,
      isLoading: isProjectLoading,
      projectMetaData,
      showProjectDetailsDrawer,
      setDrawerVisibility,
      conversations,
      isLoadingConversations,
      attachments,
      isLoadingAttachments,
      isUploadingFiles,
      fetchProject,
      fetchAttachments,
      updateProject: onUpdateProject,
      uploadFiles: onUploadFiles,
      deleteFile: onDeleteFile,
      handleDisconnectGoogleDrive: onDisconnectGoogleDrive,
    },
    sidebar,
    mode,
    firstProjectDialog: {
      show: showFirstProjectDialog,
      setShow: onSetShowFirstProjectDialog,
      onClose: onCloseFirstProjectDialog,
      onStart: onStartFirstProjectDialog,
    },
    brandSync,
  } = facade;

  // ========== Local State ==========
  const [isUploadFilesDialogOpen, setIsUploadFilesDialogOpen] = React.useState(false);
  const [isDesignSystemDrawerOpen, setIsDesignSystemDrawerOpen] = React.useState(false);
  const creditLimitBanner = useCreditLimitBanner();

  // ========== Streaming Sync State (for Google Drive) ==========
  const [isSyncingStream, setIsSyncingStream] = React.useState(false);
  const [syncStreamProgress, setSyncStreamProgress] = React.useState({
    current: 0,
    total: 0,
    fileName: '',
  });
  const [isSyncStreamComplete, setIsSyncStreamComplete] = React.useState(false);
  const [syncStreamError, setSyncStreamError] = React.useState<{
    message: string;
    requiresReauth: boolean;
  } | null>(null);
  const [isSyncToastMinimized, setIsSyncToastMinimized] = React.useState(false);
  const [isSyncToastVisible, setIsSyncToastVisible] = React.useState(false);
  const syncCleanupRef = React.useRef<(() => void) | null>(null);

  // ========== Custom Hooks ==========
  // Scroll the main content area to top on mode change
  const mainScrollRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    setTimeout(() => {
      if (mainScrollRef.current) {
        mainScrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 0);
  }, [currentMode]);
  // ========== File Attachments Hook ==========
  const fileAttachmentsHook = useFileAttachments({
    maxFiles: 10,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxTotalSize: 50 * 1024 * 1024, // 50MB
    allowedFileTypes: ['.pdf', '.txt', '.doc', '.docx', '.png', '.jpg', '.jpeg'],
  });

  // ========== Cleanup Effect ==========
  React.useEffect(() => {
    // Cleanup function: clear intervals and timeouts on unmount
    return () => {
      // Cleanup any ongoing sync
      if (syncCleanupRef.current) {
        syncCleanupRef.current();
        syncCleanupRef.current = null;
      }
    };
  }, []);

  // ========== Computed Values ==========
  const projectName = currentProject?.name || currentProject?.title || 'Project';
  const projectId = currentProject?.id || currentProject?.project_id;

  // Mode-based content
  const modeContent = React.useMemo(() => {
    if (currentMode === 'prototyper') {
      return {
        title: 'Start Prototyping, transform Your problem into possibility',
        description:
          'Describe your solution. Ideoz AI uses the context to turn great ideas into reality, fast',
        placeholder: 'Describe your UX experience prototype to generate',
      };
    }
    return {
      title: 'Exit the hype, focus on the right problem first',
      description: 'Uncover the right UX problem faster and smarter with Ideoz-AI',
      placeholder: 'Uncover the right UX problem faster and smarter with Ideoz-AI',
    };
  }, [currentMode, projectName]);

  // ========== Event Handlers ==========

  /**
   * Handle chat text area submit
   */
  const handleSubmit = (text: string, attachmentIds: string[], llmFamily?: string) => {
    if (!text.trim() && attachmentIds.length === 0) return;

    // Analytics: project_details.message.sent tracked via Redux middleware

    onResetConversation();
    onCreateConversation({
      init_message: text,
      mode: currentMode,
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      llmFamily: llmFamily,
    });

    onNavigateToConversation();
  };

  /**
   * Handle quick action chip click
   */
  const handleQuickActionClick = (action: QuickAction) => {
    // Analytics: project_details.quick_action.clicked tracked via data-track attribute

    onResetConversation();
    onCreateConversation({
      init_message: action.prompt,
      mode: currentMode,
      skip_analysis: true,
    });

    onNavigateToConversation();
  };

  /**
   * Handle project update
   */
  const handleUpdateProject = async (data: ProjectFormData) => {
    try {
      await onUpdateProject(data);
      // Success toast handled by platform page
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  };

  /**
   * Handle files upload
   */
  const handleFilesUpload = async (files: File[]) => {
    try {
      await onUploadFiles(files);
      // Analytics: file.uploaded tracked via Redux middleware
    } catch (error) {
      console.error('Failed to upload files:', error);
      throw error;
    }
  };

  /**
   * Handle file delete
   */
  const handleFileDelete = async (fileId: string) => {
    try {
      await onDeleteFile(fileId);
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  };

  /**
   * Handle conversation click
   */
  const handleConversationClick = (
    conversationId: string,
    conversationTitle?: string,
    conversationMode?: ConversationMode
  ) => {
    onResetConversation();
    onSetConversationName(conversationTitle || 'New Conversation');
    onSetConversationId(conversationId);
    onSetConversationMode(conversationMode || currentMode);
    onNavigateToConversation(conversationId);
  };

  /**
   * Handle sync start from the new GoogleFolderDialog.
   * This uses streaming sync with SSE for real-time progress updates.
   */
  const handleSyncStart = async (params: {
    folderId: string;
    folderName: string;
    accessToken: string;
    acceptedFilesCount: number;
    files: Array<{ id: string; name: string; mimeType: string; size?: number }>;
  }) => {
    const { folderId, folderName, accessToken, acceptedFilesCount, files } = params;

    // folderId is optional now since we're selecting files directly
    if (!projectId || !accessToken) {
      console.error('❌ Missing projectId or accessToken');
      toast.error('Unable to sync files. Please try again.');
      return;
    }

    if (!files || files.length === 0) {
      console.error('❌ No files to sync');
      toast.error('Please select at least one file to sync.');
      return;
    }

    // Close dialog and show sync toast
    setIsUploadFilesDialogOpen(false);
    setIsSyncToastVisible(true);
    setIsSyncingStream(true);
    setIsSyncStreamComplete(false);
    setSyncStreamError(null);
    setIsSyncToastMinimized(false);
    setSyncStreamProgress({ current: 0, total: acceptedFilesCount, fileName: '' });

    // Clean up any previous sync
    if (syncCleanupRef.current) {
      syncCleanupRef.current();
      syncCleanupRef.current = null;
    }

    try {
      // Start streaming sync
      const cleanup = googleDriveService.syncFolderWithToken(
        projectId,
        folderId,
        folderName,
        accessToken,
        files,
        // onProgress callback
        (data: { current: number; total: number; fileName: string }) => {
          setSyncStreamProgress({
            current: data.current,
            total: data.total,
            fileName: data.fileName,
          });
        },
        // onComplete callback
        () => {
          setIsSyncingStream(false);
          setIsSyncStreamComplete(true);

          // Refresh project data to show updated files
          fetchProject(projectId);

          // Hide toast after 5 seconds
          setTimeout(() => {
            setIsSyncToastVisible(false);
            setIsSyncStreamComplete(false);
          }, 5000);
        },
        // onError callback
        (error: { message: string; requiresReauth: boolean }) => {
          const isNetworkError = error?.message?.toLowerCase().includes('failed to fetch');
          setIsSyncingStream(false);
          setSyncStreamError({
            message: isNetworkError
              ? 'Network error. Please check your connection and try again.'
              : error.message,
            requiresReauth: error.requiresReauth,
          });

          // Keep toast visible to show error
          // User can dismiss or re-authenticate
        }
      );

      syncCleanupRef.current = cleanup;
    } catch (error: any) {
      console.error('❌ Error starting sync:', error);
      const message = error?.message || 'Failed to start sync';
      const isNetworkError = message.toLowerCase().includes('failed to fetch');
      setIsSyncingStream(false);
      setSyncStreamError({
        message: isNetworkError
          ? 'Network error. Please check your connection and try again.'
          : message,
        requiresReauth: false,
      });
    }
  };

  /**
   * Handle re-authentication when token expires during sync
   */
  const handleSyncReauthenticate = () => {
    // Close the toast and re-open the upload files dialog
    setIsSyncToastVisible(false);
    setSyncStreamError(null);
    setIsUploadFilesDialogOpen(true);
  };

  // Google Icon

  const googleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width="28"
      height="28"
      viewBox="0 0 48 48"
    >
      <path fill="#FFC107" d="M17 6L31 6 45 30 31 30z"></path>
      <path fill="#1976D2" d="M9.875 42L16.938 30 45 30 38 42z"></path>
      <path fill="#4CAF50" d="M3 30.125L9.875 42 24 18 17 6z"></path>
    </svg>
  );

  const driveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 13" fill="none">
      <path
        d="M6.8757 4.03217L4.71265 0.214845C4.78447 0.141723 4.8671 0.0937604 4.94973 0.0615234C4.31186 0.271457 4.01917 0.987745 4.01917 0.987745L0.0676074 7.95957C0.0120059 8.17815 -0.00344032 8.37551 0.00350987 8.54927H4.31649L6.8757 4.03217Z"
        fill="#34A853"
      />
      <path
        d="M6.87695 4.03217L9.43617 8.54927H13.7491C13.7561 8.37551 13.7406 8.17815 13.685 7.95957L9.73348 0.987745C9.73348 0.987745 9.44003 0.271457 8.80293 0.0615234C8.88479 0.0937604 8.96819 0.141723 9.04001 0.214845L6.87695 4.03217Z"
        fill="#FBBC05"
      />
      <path
        d="M6.87594 4.03214L9.039 0.214816C8.96718 0.141693 8.88378 0.0937309 8.80192 0.061494C8.70771 0.0308296 8.60654 0.0103867 8.49688 0.00488281H8.38259H5.3693H5.255C5.14612 0.00960041 5.04418 0.0300433 4.94997 0.061494C4.86811 0.0937309 4.78471 0.141693 4.71289 0.214816L6.87594 4.03214Z"
        fill="#188038"
      />
      <path
        d="M4.31764 8.54883L2.1793 12.3229C2.1793 12.3229 2.10825 12.2883 2.01172 12.2168C2.31521 12.4503 2.61021 12.4998 2.61021 12.4998H11.0084C11.4717 12.4998 11.5682 12.3229 11.5682 12.3229C11.5698 12.3221 11.5705 12.3213 11.5721 12.3205L9.4353 8.54883H4.31764Z"
        fill="#4285F4"
      />
      <path
        d="M4.31884 8.54883H0.00585938C0.0274822 9.06147 0.249889 9.36104 0.249889 9.36104L0.41206 9.64253C0.423644 9.65904 0.430593 9.66847 0.430593 9.66847L0.783509 10.288L1.57583 11.6672C1.599 11.723 1.62603 11.7741 1.6546 11.8228C1.66541 11.8394 1.67545 11.8574 1.68703 11.8732C1.69012 11.8779 1.69321 11.8826 1.6963 11.8873C1.79437 12.0257 1.90403 12.1326 2.01369 12.2168C2.11022 12.2891 2.18127 12.3229 2.18127 12.3229L4.31884 8.54883Z"
        fill="#1967D2"
      />
      <path
        d="M9.43555 8.54883H13.7485C13.7269 9.06147 13.5045 9.36104 13.5045 9.36104L13.3423 9.64253C13.3307 9.65904 13.3238 9.66847 13.3238 9.66847L12.9709 10.288L12.1786 11.6672C12.1554 11.723 12.1284 11.7741 12.0998 11.8228C12.089 11.8394 12.0789 11.8574 12.0673 11.8732C12.0643 11.8779 12.0612 11.8826 12.0581 11.8873C11.96 12.0257 11.8503 12.1326 11.7407 12.2168C11.6442 12.2891 11.5731 12.3229 11.5731 12.3229L9.43555 8.54883Z"
        fill="#EA4335"
      />
    </svg>
  );

  // ========== Header Menu Items ==========
  const headerMenuItems = React.useMemo(
    () => [
      {
        id: 'upload-files',
        label: 'Upload files',
        icon: FolderPlus as any,
        onClick: () => setIsUploadFilesDialogOpen(true),
      },
      {
        id: 'update-project-context',
        label: 'Update project context',
        icon: FileText as any,
        onClick: () => setDrawerVisibility(true),
      },
      {
        id: 'brand-sync',
        label: 'Brand Sync',
        icon: ReplaceAll as any,
        description: 'Make the generated prototype look like your brand identity.',
        disabled: !brandSync.isEnabled,
        // Only show submenu (tooltip) when disabled
        submenu: !brandSync.isEnabled
          ? {
              title: 'Brand Sync',
              subtitle: 'Paid plan feature',
              description:
                'Brand Sync allows you to update project design system to make the prototype match your brand identity.',
              actionLabel: 'Upgrade',
              onAction: brandSync.onUpgrade,
            }
          : undefined,
        // When enabled, open Design System Drawer (same as "Update your design system")
        onClick: brandSync.isEnabled ? () => setIsDesignSystemDrawerOpen(true) : () => {},
      },
    ],
    [setDrawerVisibility, brandSync, facade.navigation]
  );

  // ========== Render: Loading State ==========
  if (!currentProject && isProjectLoading) {
    return (
      <div className="h-[100vh] flex flex-col">
        <Header showBackButton backText="Loading..." onBack={onBack} isHaderMenu />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-gray-600">Loading project context...</p>
        </main>
      </div>
    );
  }

  // // ========== Render: Not Found State ==========
  // if (!currentProject && !isProjectLoading) {
  //   return (
  //     <div className="min-h-screen flex flex-col">
  //       <Header
  //         showBackButton
  //         backText="Project Not Found"
  //         onBack={onBack}
  //       />
  //       <main className="flex-1 flex items-center justify-center">
  //         <p className="text-gray-600">Project not found. Redirecting to landing page...</p>
  //       </main>
  //     </div>
  //   );
  // }

  // Prototyper Limitation: disable quick actions in prototyper mode
  const isPrototyperMode = currentMode === 'prototyper';

  // Check if user has credits remaining
  // Base credits are ALWAYS required first
  // In prototyper mode: also check prototype credits
  const prototypeCredit = planDetails?.credits?.find((c: any) => c.type === 'prototype');
  const prototypeBalance = prototypeCredit != null ? prototypeCredit.balance : 1; // If no prototype credit in planDetails, allow (don't block)

  const baseCredit = planDetails?.credits?.find((c: any) => c.type === 'base');
  const baseBalance =
    baseCredit != null
      ? typeof baseCredit.balance === 'string'
        ? parseInt(baseCredit.balance, 10)
        : (baseCredit.balance ?? 0)
      : 1; // If no base credit in planDetails, allow (don't block)

  // Base credits are always required first
  const hasBaseCredits = baseBalance > 0;
  const hasPrototypeCredits = prototypeBalance > 0;

  // Final check: base credits required, then prototype credits in prototyper mode
  // If no base credits -> block (show UpgradeCtaCard)
  // If has base credits AND in prototyper mode -> also check prototype credits
  const canProceed = hasBaseCredits && (isPrototyperMode ? hasPrototypeCredits : true);

  // ========== Render: Main Content ==========
  return (
    <AppLayout
      sidebar={sidebar}
      user={{
        isLoggedin: isLoggedIn,
        name: headerProps.user?.name,
        avatar: headerProps.user?.avatar,
      }}
      userMenuProps={{
        points: headerProps.points,
        pointsLabel: 'Points',
        menuItems: userMenuItems,
        socialLinks: userMenuSocialLinks,
        isRequestingUpgrade,
        canRequestUpgrade: true,
        isWeb,
      }}
      isWeb={isWeb}
      height="full"
      mainClassName="h-[100vh]"
      mainScrollRef={mainScrollRef}
    >
      <Header
        {...headerProps}
        showBackButton
        backText={projectName}
        onBack={handleBack}
        isHaderMenu
        isHasBorder
        menuItems={headerMenuItems}
        isWeb={isWeb}
        creditLimitBanner={
          creditLimitBanner.isVisible
            ? {
                ...creditLimitBanner,
                onAction: () => facade.navigation.toBilling(),
                onDismiss: () => creditLimitBanner.setVisible(false),
              }
            : undefined
        }
      />

      <div
        className={`max-w-landing-page mx-auto py-20 lg:py-36 ${isWeb ? 'flex flex-col justify-center' : ''}`}
      >
        {/* Title Section */}
        <div className="flex flex-col gap-1">
          <h2
            className={`text-gray-800 text-center leading-tight text-3xl font-medium}`}
            data-testid="project-title-heading"
          >
            {modeContent.title}
          </h2>
          <p
            className={`text-gray-600 text-center leading-tight text-base`}
            data-testid="project-title-description"
          >
            {modeContent.description}
          </p>
        </div>

        {/* Chat Text Area or Upgrade CTA (prototyper/free plan limit or out of credits) */}
        <div className="mt-8 mb-8 flex justify-center">
          {canProceed ? (
            <ChatTextAreaInternal
              placeholder={modeContent.placeholder}
              onSubmit={handleSubmit}
              useFileAttachmentsHook={useFileAttachments}
              isWeb={isWeb}
              analyticsContext={projectId ? { projectId, isConversation: false } : undefined}
              initialLlmFamily={llmFamily as any}
              onLlmFamilyChange={(newModel) => {
                // Update Redux state when model changes
                onSetLlmFamily(newModel);
              }}
              currentMode={currentMode}
              prototyperGenerationModel={facade.prototypeGeneration.model}
              onPrototyperGenerationModelChange={facade.prototypeGeneration.setModel}
            />
          ) : (
            <div className="w-full flex flex-col items-center justify-center">
              <UpgradeCtaCard
                className="w-11/12 rounded-lg"
                title={
                  !hasBaseCredits
                    ? "You've used all your credits"
                    : "You've used all your prototype credits"
                }
                description={
                  !hasBaseCredits
                    ? 'Upgrade your plan or purchase additional credits to continue.'
                    : 'Upgrade your plan or purchase additional prototype credits to continue.'
                }
                buttonText="Upgrade"
                onUpgrade={facade.navigation.toBilling}
                data-testid="upgrade-cta-prototyper"
              />
            </div>
          )}
        </div>

        {/* Quick Action Chips - Only in uncover mode */}
        {currentMode === 'uncover' && (
          <div className="mb-16" data-testid="quick-action-chips-section">
            <QuickActionChips
              onActionClick={handleQuickActionClick}
              isWeb={isWeb}
              testId="project-quick-actions"
              disabled={baseBalance === 0}
            />
          </div>
        )}

        {/* Need Smarter Help Section */}
        {(!currentProject?.hasUpdateDetails || !currentProject?.hasAddAttachment) && (
          <div className="flex flex-col gap-4" data-testid="need-smarter-help-section">
            <p className="text-gray-800 text-start text-base font-medium leading-[120%]">
              Need smarter help?
            </p>
            <div className="flex flex-row lg:items-center gap-4">
              {!currentProject?.hasUpdateDetails && (
                <Button
                  variant="secondary-gray"
                  size="default"
                  onClick={() => setDrawerVisibility(true)}
                  className="px-3 py-2"
                  data-testid="btn-update-project-context"
                >
                  <Icon icon={FileText} size={18} strokeWidth={2} color="#414651" />
                  Update project context
                </Button>
              )}
              {!currentProject?.hasAddAttachment && (
                <Button
                  variant="secondary-gray"
                  size="default"
                  onClick={() => setIsUploadFilesDialogOpen(true)}
                  className="px-3 py-2"
                  data-testid="btn-upload-project-files"
                >
                  <Icon icon={FolderPlus} size={18} color="#414651" strokeWidth={2} />
                  Upload project files
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <Icon icon={CircleAlert} size={18} color="#414651" strokeWidth={2} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-semibold">
                          Supported files{' '}
                          <span className="font-normal">(Maximum file size 10 MB)</span>
                        </p>
                        <p>JPG, PNG, PDF, DOC, DOCX, TXT, MD, PPT, PPTX, XLS, XLSX</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Previous Conversations List */}
        <ProjectConversationsList
          conversations={conversations}
          isLoading={isLoadingConversations}
          hasMore={false}
          onLoadMore={() => {}}
          onConversationClick={handleConversationClick}
        />
      </div>

      {/* Modal Components */}
      <ProjectDetailsModal
        isOpen={showProjectDetailsDrawer}
        onDrawerClose={() => setDrawerVisibility(false)}
        onUpdate={onUpdateProject}
        isUpdating={false}
        projectId={projectId}
        initialValues={{
          name: currentProject?.name || currentProject?.title || projectMetaData?.title || '',
          description: currentProject?.description || projectMetaData?.description || '',
          main_persona: currentProject?.mainPersona || projectMetaData?.persona || '',
          design_process: currentProject?.designProcess || 'double_diamond',
          ideoz_role: currentProject?.ideozRole || projectMetaData?.ideoz_role || '',
          user_role: currentProject?.userRole || projectMetaData?.user_role || '',
          industry: currentProject?.industry || projectMetaData?.industry || '',
        }}
        isWeb={isWeb}
      />
      <UploadFilesDialog
        isOpen={isUploadFilesDialogOpen}
        onClose={() => setIsUploadFilesDialogOpen(false)}
        projectAttachments={attachments.map((att: any) => ({
          id: att.id,
          fileName: att.file_name || att.fileName || att.name,
          isLoading: false,
          fromGoogle: att.fromGoogle ?? false,
          gdriveId: att.gdriveId,
          mimeType: att.mimeType || att.contentType,
          fileSize: att.fileSize || att.size,
        }))}
        onFilesUpload={onUploadFiles}
        onFileDelete={handleFileDelete}
        onFetchAttachments={fetchAttachments}
        isUploading={isUploadingFiles}
        loadingAttachments={isLoadingAttachments}
        showToast={showToast}
        t={t}
        isWeb={isWeb}
        planDetails={planDetails}
        googleAuthConfig={googleAuthConfig}
        projectId={projectId}
        onGoogleSyncStart={handleSyncStart}
      />

      {/* Sync Progress Toast */}
      {isWeb && (
        <SyncProgressToast
          isVisible={isSyncToastVisible}
          isMinimized={isSyncToastMinimized}
          currentFile={syncStreamProgress.current}
          totalFiles={syncStreamProgress.total}
          currentFileName={syncStreamProgress.fileName}
          isComplete={isSyncStreamComplete}
          error={syncStreamError}
          onMinimize={() => setIsSyncToastMinimized(true)}
          onExpand={() => setIsSyncToastMinimized(false)}
          onClose={() => {
            setIsSyncToastVisible(false);
            setSyncStreamError(null);
            // Cleanup any ongoing sync
            if (syncCleanupRef.current) {
              syncCleanupRef.current();
              syncCleanupRef.current = null;
            }
          }}
          onReauthenticate={handleSyncReauthenticate}
        />
      )}

      {/* Design System Drawer (organism) */}
      <DesignSystemDrawer
        open={isDesignSystemDrawerOpen}
        projectId={projectId}
        onClose={() => setIsDesignSystemDrawerOpen(false)}
        onUpdated={(payload) => {
          // notify or refresh data as needed; for now we'll log and close drawer
          console.log('Design system updated', payload);
        }}
      />

      {/* First Project Welcome Dialog */}
      <CreateFirstProjectDialog
        open={false} // should be showFirstProjectDialog
        onOpenChange={onSetShowFirstProjectDialog}
        onStart={onStartFirstProjectDialog}
      />
    </AppLayout>
  );
};

export default ProjectDetailsTemplate;
